"use client";

import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import bannerBgShape from "@/assets/BannerBgShape.svg";
import Image from "next/image";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { PrismicNextImage } from "@prismicio/next";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { animate, AnimatePresence, motion } from "framer-motion";
import heroBg from "@/assets/hero-bg.svg";
import heroBgMobile from "@/assets/hero-bg-mobile.svg";
import RightSideImgBgShadow from "@/assets/RightSideImgBgShadow.svg";
import RightSideImgBgShadowMobile from "@/assets/RightSideImgBgShadowMobile.svg";
import { useMediaQuery } from "usehooks-ts";
import { MotionButton } from "@/components/ui/motion-button";

export type HeroProps = SliceComponentProps<Content.HeroSlice>;

const Hero: FC<HeroProps> = ({ slice }) => {
  const [index, setIndex] = useState(0);
  const [mounted, setMounted] = useState<boolean>(false);
  const isLargerDevice = useMediaQuery("(min-width: 768px)");
  const [exploreCampusesButtonHovered, setExploreCampusesButtonHovered] =
    useState<boolean>(false);
  const topNumberValueRef = useRef<HTMLSpanElement | null>(null);
  const middleNumberValueRef = useRef<HTMLSpanElement | null>(null);
  const bottomNumberValueRef = useRef<HTMLSpanElement | null>(null);

  const animateFrom0ToN = useCallback(
    (
      ref: React.MutableRefObject<HTMLSpanElement | null>,
      targetNum: number,
      duration: number = 2,
      suffix: string = '+'
    ) => {
      const node = ref.current;

      if (node) {
        node.textContent = `0${suffix}`;

        const controls = animate(0, targetNum, {
          duration,
          ease: "easeOut",
          onUpdate: (latestValue) => {
            node.textContent = `${latestValue.toFixed(0)}${suffix}`;
          },
        });

        return () => controls.stop();
      }
    },
    []
  );

  useEffect(() => {
    if (mounted) {
      animateFrom0ToN(topNumberValueRef, slice.primary.university_count || 0, 2, '+');
      animateFrom0ToN(
        middleNumberValueRef,
        slice.primary.commitment_percent || 0,
        2, '%'
      );
      animateFrom0ToN(
        bottomNumberValueRef,
        slice.primary.students_count || 0,
        2, 'k+'
      );
    }
  }, [
    mounted,
    animateFrom0ToN,
    slice.primary.university_count,
    slice.primary.commitment_percent,
    slice.primary.students_count
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % slice.primary.title_middle_parts.length);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative bg-[#033D61] mt-[104px] w-full overflow-hidden"
    >
      <Image
        src={bannerBgShape}
        alt="shape"
        width={1225}
        height={1137}
        priority
        fetchPriority="high"
        className="absolute left-[-436.351px] top-0 w-full h-[785px] object-cover pointer-events-none select-none"
      />
      <Bounded as={"div"} className="relative ">
        <div className="absolute bg-[#69E1CA] blur-[137px] h-[335px] w-[335px] rounded-full right-[-130px] top-[-74px]" />
        <div className="absolute bg-[#69E1CA] blur-[137px] h-[349px] w-[349px] rounded-full bottom-[-187px] left-[-163px]" />
        {/* contents  */}
        <div className="xl:flex xl:items-start xl:justify-between">
          {/* shape and content */}
          <div className="xl:flex-1 flex flex-col justify-center items-center">
            {/* text on top of the shape */}
            <div className="h-[785px] flex flex-col justify-center items-start text-left text-white ">
              <div className="space-y-6">
                <div className="text-5xl md:text-6xl leading-16 font-archivo font-semibold">
                  <PrismicRichText
                    field={slice.primary.title_top_part}
                    components={{
                      label: ({ node, children }) => {
                        if (node.data.label === "Highlight") {
                          return (
                            <span className="text-secondary">{children}</span>
                          );
                        }
                        return <span className="text-white">{children}</span>;
                      },
                    }}
                  />
                  <div className="relative sm:h-16 h-32 overflow-hidden">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={index}
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "-100%" }}
                        transition={{
                          duration: 0.5,
                          ease: "easeInOut",
                        }}
                        className="absolute inset-0"
                      >
                        <PrismicRichText
                          field={
                            slice.primary.title_middle_parts[index].content
                          }
                          components={{
                            label: ({ node, children }) => {
                              if (node.data.label === "Highlight") {
                                return (
                                  <span className="text-secondary">
                                    {children}
                                  </span>
                                );
                              }
                              return (
                                <span className="text-white">{children}</span>
                              );
                            },
                          }}
                        />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                  <PrismicRichText
                    field={slice.primary.title_bottom_part}
                    components={{
                      label: ({ node, children }) => {
                        if (node.data.label === "Highlight") {
                          return (
                            <span className="text-secondary">{children}</span>
                          );
                        }
                        return <span className="text-white">{children}</span>;
                      },
                    }}
                  />
                </div>
                <PrismicRichText
                  field={slice.primary.sub_title}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-[#EBF7FF] text-lg md:text-xl leading-8 font-merriweather tracking-wider">
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>
              <div className="flex items-center flex-col md:flex-row gap-4 mt-16 z-20 w-full">
                {/* <ActionButton btnTxt={slice.primary.left_button.text || ""} /> */}
                <div className="md:w-fit w-full">
                  <MotionButton
                    className={`cursor-pointer font-archivo rounded-full text-primary flex items-center justify-center gap-1.5 leading-6 font-medium h-fit py-3.5 px-4 md:w-fit w-full transition-all duration-300 ease-in-out`}
                    style={{
                      background:
                        "radial-gradient(circle at left, #43D9BD 0%, white 0%)",
                      backgroundRepeat: "no-repeat",
                    }}
                    whileHover={{
                      background:
                        "radial-gradient(circle at left, #43D9BD 100%, white 100%)",
                    }}
                    transition={{
                      duration: 0.5,
                      ease: "easeIn",
                    }}
                  >
                    <span className="text-[16px]  transition-all duration-300 ease-in-out whitespace-nowrap text-center">
                      {slice.primary.left_button.text || "Explore Programmes"}
                    </span>
                    <ArrowRight className="  size-6! shrink-0  rounded-full bg-white text-primary! transition-all duration-300 ease-in-out" />
                  </MotionButton>
                </div>
                <div className="md:w-fit w-full">
                  <Button
                    onMouseEnter={() => setExploreCampusesButtonHovered(true)}
                    onMouseLeave={() => setExploreCampusesButtonHovered(false)}
                    className={`cursor-pointer font-archivo  bg-transparent border border-white rounded-full text-white flex items-center justify-center gap-1.5 leading-6 font-medium h-fit py-3.5 px-4  md:w-fit w-full transition-all duration-300 ease-in-out`}
                  >
                    <span className="text-[16px]  transition-all duration-300 ease-in-out whitespace-nowrap text-center">
                      {slice.primary.right_button.text || "Explore Campuses"}
                    </span>
                    {/* video icon */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="27"
                      height="27"
                      viewBox="0 0 27 27"
                      fill="none"
                      className="size-6! shrink-0  rounded-full bg-white text-primary! transition-all duration-300 ease-in-out"
                    >
                      <circle cx="13.25" cy="13.25" r="9" fill="#36AE97" />
                      <path
                        d="M25.25 13.25C25.25 16.4326 23.9857 19.4848 21.7353 21.7353C19.4848 23.9857 16.4326 25.25 13.25 25.25C10.0674 25.25 7.01516 23.9857 4.76472 21.7353C2.51428 19.4848 1.25 16.4326 1.25 13.25C1.25 10.0674 2.51428 7.01516 4.76472 4.76472C7.01516 2.51428 10.0674 1.25 13.25 1.25C16.4326 1.25 19.4848 2.51428 21.7353 4.76472C23.9857 7.01516 25.25 10.0674 25.25 13.25ZM11.435 8.8895C11.3229 8.80965 11.1909 8.76221 11.0536 8.75238C10.9163 8.74254 10.7789 8.77069 10.6565 8.83374C10.5341 8.89679 10.4315 8.99231 10.3598 9.10983C10.2881 9.22735 10.2501 9.36233 10.25 9.5V17C10.2501 17.1377 10.2881 17.2727 10.3598 17.3902C10.4315 17.5077 10.5341 17.6032 10.6565 17.6663C10.7789 17.7293 10.9163 17.7575 11.0536 17.7476C11.1909 17.7378 11.3229 17.6903 11.435 17.6105L16.685 13.8605C16.7822 13.7911 16.8615 13.6995 16.9161 13.5933C16.9708 13.4872 16.9994 13.3694 16.9994 13.25C16.9994 13.1306 16.9708 13.0128 16.9161 12.9067C16.8615 12.8005 16.7822 12.7089 16.685 12.6395L11.435 8.8895Z"
                        fill="#D9F7F2"
                      />
                      <path
                        d="M25.25 13.25C25.25 14.8259 24.9396 16.3863 24.3366 17.8422C23.7335 19.2981 22.8496 20.621 21.7353 21.7353C20.621 22.8496 19.2981 23.7335 17.8422 24.3366C16.3863 24.9396 14.8259 25.25 13.25 25.25C11.6741 25.25 10.1137 24.9396 8.6578 24.3366C7.20189 23.7335 5.87902 22.8496 4.76472 21.7353C3.65042 20.621 2.7665 19.2981 2.16345 17.8422C1.56039 16.3863 1.25 14.8259 1.25 13.25C1.25 11.6741 1.56039 10.1137 2.16345 8.6578C2.7665 7.20189 3.65042 5.87902 4.76472 4.76472C5.87902 3.65041 7.20189 2.7665 8.6578 2.16345C10.1137 1.56039 11.6741 1.25 13.25 1.25C14.8259 1.25 16.3863 1.56039 17.8422 2.16345C19.2981 2.7665 20.621 3.65042 21.7353 4.76472C22.8496 5.87902 23.7335 7.20189 24.3366 8.6578C24.9396 10.1137 25.25 11.6741 25.25 13.25L25.25 13.25Z"
                        stroke="#B4F0E5"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                      <motion.path
                        d="M25.25 13.25C25.25 16.1717 24.1841 18.993 22.2522 21.1848C20.3203 23.3766 17.6551 24.7882 14.7566 25.1551C11.858 25.5219 8.9252 24.8186 6.50821 23.1772C4.09122 21.5357 2.3561 19.0689 1.62829 16.2393C0.900479 13.4097 1.22998 10.4118 2.55498 7.80789C3.87999 5.20394 6.10947 3.17284 8.82528 2.09555C11.5411 1.01825 14.5566 0.968759 17.3063 1.95636C20.056 2.94397 22.3509 4.90081 23.7607 7.45988"
                        stroke="#36AE97"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={
                          exploreCampusesButtonHovered
                            ? { pathLength: 1 }
                            : { pathLength: 0 }
                        }
                        transition={{
                          duration: 1.2,
                          ease: "easeInOut",
                        }}
                      />
                    </svg>
                  </Button>
                </div>
                {/* <ActionButton btnTxt={slice.primary.right_button.text || ""} /> */}
              </div>
            </div>
          </div>

          {/* Right side */}
          <motion.div
            className="xl:flex-1 xl:h-full xl:w-full w-fit text-6xl my-auto flex flex-col items-center justify-center ml-auto"
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {mounted && (
              <div className="relative">
                <div
                  className="relative w-[354px] h-[332px] md:w-[581px] md:h-[481px] flex items-center justify-end overflow-hidden z-10"
                  style={{
                    clipPath: isLargerDevice
                      ? "path('M473.738 26.2296C365.543 56.4707 292.377 21.3437 269.318 0C261.028 9.51464 238.073 36.5671 212.579 68.6597C251.442 98.7466 289.528 108.776 316.732 118.033C241.337 111.861 226.569 171.264 226.569 176.664C224.082 218.631 25.9087 136.805 43.5266 146.577L0 176.664L1.55452 248.409C199.316 570.453 250.509 370.3 271.65 382.643C250.664 411.187 242.114 443.588 236.673 457.474C259.991 462.103 298.077 475.218 322.949 480.618C326.836 459.789 336.163 446.674 353.263 418.902C371.917 427.388 434.098 438.188 445.757 438.188C540.582 439.731 568.564 369.528 570.896 361.042C586.441 302.411 542.655 273.353 518.819 266.153C523.224 260.238 534.364 244.552 543.692 229.123C568.564 179.75 540.582 152.749 532.033 142.72C567.476 110.627 579.445 68.6597 581 51.6877L518.819 7.71458C510.528 10.8004 489.905 18.8236 473.738 26.2296Z')"
                      : "path('M289.295 16.0243C223.224 34.4994 178.544 13.0394 164.463 0C159.4 5.81273 145.383 22.3397 129.814 41.9459C153.546 60.3267 176.804 66.4537 193.417 72.1093C147.376 68.3389 138.358 104.629 138.358 107.928C136.839 133.567 15.8215 83.5777 26.5801 89.5475L0 107.928L0.94929 151.759C121.715 348.504 152.977 226.225 165.887 233.766C153.072 251.204 147.85 270.999 144.528 279.482C158.767 282.31 182.025 290.322 197.214 293.622C199.587 280.896 205.283 272.884 215.725 255.917C227.116 261.102 265.088 267.7 272.207 267.7C330.114 268.643 347.201 225.754 348.625 220.57C358.118 184.751 331.38 166.998 316.824 162.599C319.514 158.986 326.317 149.403 332.013 139.977C347.201 109.814 330.114 93.3179 324.893 87.191C346.537 67.5848 353.846 41.9459 354.796 31.5773L316.824 4.71303C311.761 6.59824 299.167 11.4998 289.295 16.0243Z')",
                  }}
                >
                  <Image
                    src={isLargerDevice ? heroBg.src : heroBgMobile.src}
                    alt="about section bg image"
                    className="absolute right-0"
                    width={isLargerDevice ? 369 : 225}
                    height={isLargerDevice ? 481 : 293}
                  />
                  <PrismicNextImage
                    field={slice.primary.right_side_image}
                    className="absolute -right-5 md:right-[-35px] -top-10 md:top-[-100px]! h-[331px] min-w-[387px] md:h-[580px] md:min-w-[635px] object-cover object-right"
                  />
                </div>
                <div className="absolute inset-0 top-[-50px]">
                  <div className="relative w-full h-full">
                    <div
                      className="w-full h-[190px]"
                      style={{ clipPath: "inset(0 0 50%)" }}
                    >
                      <PrismicNextImage
                        className="absolute -right-5 md:right-[-35px] top-2.5 md:top-[-50px]! h-[331px] min-w-[387px] md:h-[580px] md:min-w-[635px] object-cover object-right"
                        field={slice.primary.right_side_image}
                      />
                    </div>
                  </div>
                </div>
                <Image
                  src={
                    isLargerDevice
                      ? RightSideImgBgShadow.src
                      : RightSideImgBgShadowMobile.src
                  }
                  alt="right bottom shape"
                  width={311}
                  height={302.5}
                  className="absolute right-[-60px] md:right-[-82px] bottom-[-120px]"
                />
                <div className="px-5 py-4 rounded-[12px] bg-[#E6F5FE45] text-[#FAFDFF] absolute top-0 left-0 md:left-15 text-center font-merriweather text-sm z-20 w-auto backdrop-blur-[10px]">
                  <span ref={topNumberValueRef} className="text-xl font-semibold">
                    {slice.primary.university_count}+
                  </span>{" "}
                  University
                  <div>{slice.primary.left_top_achievement_sub_title}</div>
                </div>
                <div className="px-5 py-4 rounded-[12px] bg-[#E6F5FE45] text-[#FAFDFF] absolute top-[50%] right-0 text-center font-merriweather text-sm z-20 w-auto backdrop-blur-[10px]">
                  <span ref={middleNumberValueRef} className="text-xl font-semibold">
                    {slice.primary.commitment_percent}%
                  </span>{" "}
                  Commitment
                  <div>{slice.primary.right_center_achievement_sub_title}</div>
                </div>
                <div className="px-5 py-4 rounded-[12px] bg-[#E6F5FE45] text-[#FAFDFF] absolute bottom-0 left-[50%] translate-x-[-50%] text-center font-merriweather text-sm z-20 w-auto backdrop-blur-[10px]">
                  <span ref={bottomNumberValueRef} className="text-xl font-semibold">
                    {slice.primary.students_count}k+
                  </span>{" "}
                  Students
                  <div>{slice.primary.bottm_center_achievement_sub_title}</div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </Bounded>
    </section>
  );
};

export default Hero;
